import {useCompanyTariffGuard} from '../../modules/company-tariff';
import {FC, useContext, useEffect, useState} from 'react';
import {Controller, SubmitHandler, useForm} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import {useRecoilStateLoadable, useRecoilValue, useSetRecoilState} from 'recoil';

import LoadingButton from '@mui/lab/LoadingButton';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import {DatePicker, LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFnsV3';
import {ru} from 'date-fns/locale';
import ControlledInput from '../../ui/controlled-input/ControlledInput';

import {ContractService} from '../../api/services/contract.service';
import {UserAccountService} from '../../api/services/user-account.service';

import {ICompany} from '../../shared/types/company.types';
import {IContractFormInputs} from '../../shared/types/contract.types';

import {contractsQueryTrigger} from '../../store/contracts/atoms';

import {TextField, Typography} from '@mui/material';
import {useGetContractsQueryRefresh} from '../../global/services/contracts';
import {useSearchCompanies} from '../../hooks/useSearchCompanies';
import {EventBusContext} from '../../providers/EventBus';
import {CONTRACT_CREATE_ROLES} from '../../shared/constants';
import {checkAuthorities} from '../../shared/helpers/checkAuthorities';
import {formatManagerFio} from '../../shared/helpers/formatManagerFio';
import {IDeviceTypesDict} from '../../shared/types/deviceTypesDict.types';
import {IUserAccount, TypeAccountRoles} from '../../shared/types/user-account.types';
import {authState} from '../../store/auth/atoms';
import {deviceTypesDictQuery} from '../../store/deviceTypes/selectors';

const dividerStyle = {letterSpacing: 1.75, fontWeight: 600, color: '#696969', fontSize: '14px'};
const dateFieldSx = {'& .MuiInputBase-root': {height: 40}};

type ContractFormValues = IContractFormInputs<Date> & {
  managers?: IUserAccount[];
};

export const AddContractForm: FC = () => {
  const {checkTariff, tariffLoading} = useCompanyTariffGuard();
  const {user} = useRecoilValue(authState);
  const triggerContractsQuery = useSetRecoilState(contractsQueryTrigger);
  const {
    handleSubmit,
    control,
    trigger,
    reset,
    setValue,
    clearErrors,
    setError,
    formState: {errors},
  } = useForm<ContractFormValues>({
    defaultValues: {
      startDate: null,
      dueDate: null,
      managers: [],
    },
    mode: 'onChange',
  });
  const navigate = useNavigate();
  const {handleSearch, data, searchTerm, searchLoading} = useSearchCompanies({fullSize: true});
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [dateError, setDateError] = useState<any>();
  const [managerOptions, setManagerOptions] = useState<IUserAccount[]>([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const {bus} = useContext(EventBusContext);
  const [{contents: serviceTypes, state: serviceTypesState}] = useRecoilStateLoadable(deviceTypesDictQuery);
  const refetchGetContractsQuery = useGetContractsQueryRefresh();
  const canCreateContract = checkAuthorities(CONTRACT_CREATE_ROLES, [...(user?.roles || [])]);
  const canEditExecutor = checkAuthorities(
    [
      TypeAccountRoles.MANAGER,
      TypeAccountRoles.PROJECT_MANAGER,
      TypeAccountRoles.DIRECTOR,
      TypeAccountRoles.FUNCTIONAL_ADMIN,
      TypeAccountRoles.ADMIN,
      TypeAccountRoles.OWNER,
    ],
    [...(user?.roles || [])],
  );

  useEffect(() => {
    let active = true;
    const loadManagers = async () => {
      try {
        setManagersLoading(true);
        const res = await UserAccountService.getAll({
          spec: {
            roles: [TypeAccountRoles.MANAGER],
            isActive: true,
          },
        });
        if (active) {
          setManagerOptions(res.data.content ?? []);
        }
      } catch (err) {
        if (active) {
          setManagerOptions([]);
        }
      } finally {
        if (active) {
          setManagersLoading(false);
        }
      }
    };

    loadManagers();

    return () => {
      active = false;
    };
  }, []);

  const serviceTypesOptions =
    serviceTypesState === 'hasValue' && serviceTypes.content?.length
      ? (serviceTypes.content.reduce((acc: any, curr: IDeviceTypesDict) => {
          if (!curr.higherCode) {
            return [
              ...acc,
              {
                label: curr.name,
                value: curr,
              },
            ];
          }

          return acc;
        }, []) as any[])
      : [];

  const onSubmit: SubmitHandler<ContractFormValues> = async (data) => {
    if (!canCreateContract) {
      toast.error('Недостаточно прав для создания контракта', {
        containerId: 'default',
        autoClose: 2000,
        hideProgressBar: true,
        pauseOnHover: false,
        draggable: false,
      });
      return;
    }
    if (!data.serviceType) {
      setError('serviceType', {type: 'required', message: 'Обязательно для заполнения'});

      return;
    }

    try {
      setLoading(true);
      const managerUids = (data.managers ?? [])
        .map((manager: IUserAccount) => manager.uid)
        .filter((uid): uid is string => Boolean(uid));
      await ContractService.create({
        companyId: data.client.id,
        description: data.description,
        executor: data.executor || undefined,
        dueDate: data.dueDate?.toISOString() || null,
        startDate: data.startDate?.toISOString() || null,
        contractNumber: data.contractNumber || null,
        serviceType: data.serviceType,
        managers: managerUids,
      });

      toast.success('Контракт добавлен', {
        containerId: 'default',
        autoClose: 2000,
        hideProgressBar: true,
        pauseOnHover: false,
        draggable: false,
      });

      setTimeout(() => {
        setLoading(false);
        triggerContractsQuery((v) => v + 1);
        reset();
        refetchGetContractsQuery();
        navigate('/contracts');
      }, 1500);
    } catch (err: any) {
      bus?.emit('error', [err]);

      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/contracts');
  };

  return (
    <LocalizationProvider adapterLocale={ru} dateAdapter={AdapterDateFns}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (checkTariff()) void handleSubmit(onSubmit)(event);
        }}
      >
        <Stack direction="column" spacing={2}>
          <Divider sx={dividerStyle} textAlign="left">
            Общая информация
          </Divider>
          <Stack direction="column" spacing={2}>
            <Stack direction="row" spacing={2}>
              <ControlledInput
                control={control}
                name="contractNumber"
                label="Номер договора"
                size="small"
                rules={{
                  required: 'Обязательно для заполнения',
                }}
                sx={{flexGrow: 5}}
              />
              <Controller
                control={control}
                name="startDate"
                rules={{
                  required: 'Обязательно для заполнения',
                }}
                render={({field: {value, onChange}, formState: {errors}}) => (
                  <DatePicker
                    label="Дата заключения"
                    format="dd.MM.yyyy"
                    value={value}
                    onError={(reason) => setDateError(reason)}
                    disableFuture
                    onChange={onChange}
                    slotProps={{
                      textField: {
                        helperText: errors?.startDate?.message,
                        error: !!errors?.startDate?.message || !!dateError,
                        size: 'small',
                        sx: dateFieldSx,
                      },
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name="dueDate"
                render={({field: {value, onChange}}) => (
                  <DatePicker
                    label="Дата окончания"
                    format="dd.MM.yyyy"
                    value={value}
                    onError={(reason) => setDateError(reason)}
                    disablePast
                    onChange={onChange}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: dateFieldSx,
                      },
                    }}
                  />
                )}
              />
            </Stack>
            <ControlledInput
              control={control}
              name="description"
              multiline
              rows={4}
              rules={{
                maxLength: {
                  value: 400,
                  message: 'Максимум 400 символов',
                },
              }}
              label="Описание контракта"
              additionalFunc={() => {
                trigger('description');
              }}
            />
            <Stack direction="row" sx={{width: {xs: '100%', md: '30%'}}}>
              <Autocomplete
                fullWidth
                disablePortal
                options={serviceTypesOptions}
                size="small"
                onChange={(_, val) => {
                  if (val) {
                    setValue('serviceType', (val as (typeof serviceTypesOptions)[0])?.value, {
                      shouldDirty: true,
                    });
                    clearErrors('serviceType');
                  }
                }}
                multiple={false}
                openOnFocus
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Вид работ"
                    error={!!errors?.serviceType}
                    helperText={errors?.serviceType ? String(errors?.serviceType?.message) : ''}
                  />
                )}
                noOptionsText={
                  <Stack direction="column" spacing={3}>
                    <Typography variant="body2" sx={{color: (t) => t.palette.text.secondary, whiteSpace: 'wrap'}}>
                      Виды работ не найдены
                    </Typography>
                  </Stack>
                }
              />
            </Stack>
            <Divider sx={dividerStyle} textAlign="left">
              Заказчик
            </Divider>
            <Controller
              control={control}
              rules={{
                required: 'Обязательно для заполнения',
              }}
              name="client"
              render={({field: {value}, fieldState: {error}}) => (
                <Autocomplete
                  open={open}
                  onOpen={() => {
                    setOpen(true);
                  }}
                  onClose={() => {
                    setOpen(false);
                  }}
                  onChange={(_, newValue) => {
                    setValue('client', newValue as ICompany);
                  }}
                  value={value}
                  getOptionLabel={(option) => `${option.fullCompanyName},   ИНН: ${option.inn}`}
                  inputValue={searchTerm}
                  onInputChange={(_, newInputValue) => {
                    handleSearch(newInputValue);
                  }}
                  size="small"
                  options={data}
                  loading={searchLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Выбор компании"
                      placeholder="Название / ИНН / комментарий"
                      error={!!error}
                      helperText={error ? error.message : ''}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
            <Divider sx={dividerStyle} textAlign="left">
              Исполнитель
            </Divider>
            <ControlledInput
              control={control}
              name="executor"
              size="small"
              label="Выбор исполнителя"
              disabled={!canEditExecutor}
            />
            <Divider sx={dividerStyle} textAlign="left">
              Менеджеры по контракту
            </Divider>
            <Controller
              control={control}
              name="managers"
              render={({field: {value, onChange}}) => (
                <Autocomplete
                  multiple
                  fullWidth
                  options={managerOptions}
                  value={value ?? []}
                  disableCloseOnSelect
                  disablePortal
                  ListboxProps={{style: {maxHeight: 240}}}
                  loading={managersLoading}
                  size="small"
                  getOptionLabel={(option) => formatManagerFio(option)}
                  isOptionEqualToValue={(option, value) => option.uid === value.uid}
                  onChange={(_, newValue) => onChange(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Выбор менеджера"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {managersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
            <Stack direction="row" sx={{justifyContent: 'space-between'}}>
              <Button onClick={handleCancel} color="error" variant="outlined" disabled={loading}>
                Отменить
              </Button>
              <LoadingButton
                disabled={tariffLoading || !!dateError || !canCreateContract}
                type="submit"
                loading={loading}
                variant="outlined"
              >
                Добавить в систему
              </LoadingButton>
            </Stack>
          </Stack>
        </Stack>
      </form>
    </LocalizationProvider>
  );
};
